import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Card from '../../../../components/common/Card/index'
import Typography from '../../../../components/common/Typography/index'
import Button from '../../../../components/common/Button/index'
import MiniHeader from '../../../../components/layout/MiniHeader'
import useCustomer from '../../hooks/index'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import Backdrop from '../../../../components/common/Backdrop/Backdrop'
import { Avatar } from '@mui/material'
import { formatDateTime } from '../../../../shared/utils/dateUtils'
import { MdRefresh, MdOutlineVerified } from 'react-icons/md'
import { IoIosArrowBack } from 'react-icons/io'
import { IoIosArrowForward } from 'react-icons/io'
import { deactivateAccount, blockAccount, unblockAccount } from '../../../../state/redux/actions/userActions'
import { getGdprConsents, getRecentActivities } from '../../../../state/redux/actions/activityActions'

const tabs = [{ id: 'activity', label: 'Activities' }]

const CustomerProfile = ({ onSave }) => {
  const { customerId } = useParams()
  const [isEditMode, setIsEditMode] = useState(false)
  const { customer, loading, error, fetchCustomer } = useCustomer(customerId)
  const [activeTab, setActiveTab] = useState('activity')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Redux selectors for GDPR data and activities
  const gdprConsents = useSelector(state => state.activity.gdprConsents)
  const recentActivities = useSelector(state => state.activity.recentActivities)
  const recentActivitiesPagination = useSelector(state => state.activity.recentActivitiesPagination)
  const activitiesLoading = useSelector(state => state.activity.loading)
  const activitiesError = useSelector(state => state.activity.error)
  const [deactivateFormOpen, setDeactivateFormOpen] = useState(false)
  const [deactivateMessage, setDeactivateMessage] = useState('')
  const [deactivatingCustomer, setDeactivatingCustomer] = useState(false)
  const [deactivateToastOpen, setDeactivateToastOpen] = useState(false)
  const [formData, setFormData] = useState({
    name: customer?.full_name,
    email: customer?.email,
    role: customer?.role,
    status: customer?.status,
  })

  useEffect(() => {
    if (!customerId || !/^[a-f\d]{24}$/i.test(customerId)) {
      navigate('/customers')
      return
    }
    fetchCustomer(customerId)
    dispatch(getGdprConsents(customerId))
    dispatch(getRecentActivities({ page: currentPage, limit: itemsPerPage }))
    // eslint-disable-next-line
  }, [customerId])

  const handleRefresh = () => {
    setCurrentPage(1)
    dispatch(fetchCustomer({ limit: itemsPerPage, page: currentPage }))
  }

  useEffect(() => {
    setFormData({
      name: customer?.full_name,
      email: customer?.email,
    })
  }, [customer])

  const handleSave = () => {
    onSave(formData)
    setIsEditMode(false)
  }

  const handleDeactivate = async () => {
    if (!deactivateMessage.trim()) {
      alert('Please enter a deactivation message.')
      return
    }

    try {
      setDeactivatingCustomer(true)
      await dispatch(
        deactivateAccount({
          userId: customerId,
          message: deactivateMessage.trim(),
        })
      ).unwrap()

      setDeactivateFormOpen(false)
      setDeactivateMessage('')
      setDeactivateToastOpen(true)

      // Refresh customer data to show updated status
      await fetchCustomer(customerId)
    } catch (error) {
      console.error('Deactivation failed:', error)
      alert(`Failed to deactivate account: ${error.message || 'Unknown error'}`)
    } finally {
      setDeactivatingCustomer(false)
    }
  }

  const handleBlock = async () => {
    const isBlocked = customer?.block || customer?.isBlocked
    const action = isBlocked ? 'unblock' : 'block'

    if (!window.confirm(`Are you sure you want to ${action} this user account?`)) {
      return
    }

    try {
      if (isBlocked) {
        await dispatch(unblockAccount(customerId)).unwrap()
        alert('User account unblocked successfully')
      } else {
        await dispatch(blockAccount(customerId)).unwrap()
        alert('User account blocked successfully')
      }
      fetchCustomer(customerId)
    } catch (error) {
      console.error(`${action} failed:`, error)
      alert(`Failed to ${action} account: ` + (error.message || 'Unknown error'))
    }
  }

  return (
    <>
      <MiniHeader title="User Management" />
      <div className="ml-[250px] p-8 max-w-7xl mx-auto">
        {loading && <LoadingIndicator />}
        {!loading && error && <ErrorState error={error} />}
        {!loading && !error && !customer && (
          <Card variant="outlined" className="text-center p-8 border border-border rounded-md max-w-md w-full">
            <Typography variant="body" className=" text-secondary ">
              Cannot find customer with ID: {customerId || 'Invalid ID'}, Check for typos
            </Typography>
          </Card>
        )}
        {!loading && !error && customer && (
          <>
            {/* header */}
            <div className="bg-white rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar src={customer.image} alt="profile" className="w-16 h-16 rounded-full" />
                  <div>
                    <h1 className="text-md font-semibold text-gray-900">{customer.full_name}</h1>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-1.5 rounded-md font-medium text-sm"
                    onClick={() => console.log('Export clicked')}
                  >
                    Export
                  </Button>
                  <Button
                    className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-1.5 rounded-md font-medium text-sm"
                    onClick={() => navigate(`/chats/${customerId}`)}
                  >
                    View Chats
                  </Button>
                  <Button
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md font-medium text-sm"
                    onClick={() => setDeactivateFormOpen(true)}
                  >
                    Deactivate
                  </Button>
                  <Button
                    className={
                      customer?.block || customer?.isBlocked
                        ? 'bg-teal-500 hover:bg-teal-600 text-white px-4 py-1.5 rounded-md font-medium text-sm'
                        : 'bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md font-medium text-sm'
                    }
                    onClick={handleBlock}
                  >
                    {customer?.block || customer?.isBlocked ? 'Unblock' : 'Block'}
                  </Button>
                </div>
              </div>
            </div>

            {/* deactivate form */}
            {deactivateFormOpen && (
              <Backdrop isOpen={deactivateFormOpen} onClose={() => setDeactivateFormOpen(false)}>
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="mb-4">
                    <Typography variant="h3" className="mb-2">
                      <strong>Deactivate Customer Account</strong>
                    </Typography>
                    <Typography variant="body" className="text-secondary mb-4">
                      Are you sure you want to deactivate {customer?.full_name}'s account? Please provide a reason for
                      deactivation.
                    </Typography>
                  </div>

                  <div className="mb-4">
                    <textarea
                      value={deactivateMessage}
                      onChange={e => setDeactivateMessage(e.target.value)}
                      placeholder="Enter deactivation reason/message..."
                      className="w-full p-3 border border-gray-300 rounded-md resize-none min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      disabled={deactivatingCustomer}
                    />
                  </div>

                  <div className="flex gap-3 justify-end">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setDeactivateFormOpen(false)
                        setDeactivateMessage('')
                      }}
                      disabled={deactivatingCustomer}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleDeactivate}
                      disabled={deactivatingCustomer || !deactivateMessage.trim()}
                    >
                      {deactivatingCustomer ? 'Deactivating...' : 'Deactivate Account'}
                    </Button>
                  </div>
                </div>
              </Backdrop>
            )}

            {/* deactivate success toast */}
            {deactivateToastOpen && (
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="flex justify-center items-center">
                  <Card className="p-8 justify-center items-center" size={96}>
                    <div className="flex gap-2 justify-center items-center mb-4">
                      <MdOutlineVerified className="text-primary text-4xl" />
                      <Typography>Account Successfully Deactivated</Typography>
                    </div>
                    <div className="flex px-8 justify-center items-center">
                      <Button size="medium" onClick={() => setDeactivateToastOpen(false)}>
                        Done
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            )}

            {/* details */}
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Info</h2>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-start gap-8">
                <div className="flex-1">
                  <Typography className="text-gray-500 text-sm font-medium mb-1">Name</Typography>
                  <Typography className="text-gray-900 text-sm">{customer?.full_name}</Typography>
                </div>
                <div className="flex-1">
                  <Typography className="text-gray-500 text-sm font-medium mb-1">Email</Typography>
                  <Typography className="text-gray-900 text-sm">{customer?.email}</Typography>
                </div>
                <div className="flex-1">
                  <Typography className="text-gray-500 text-sm font-medium mb-1">Phone</Typography>
                  <Typography className="text-gray-900 text-sm">{customer?.phoneNumber || 'N/A'}</Typography>
                </div>
                <div className="flex-1">
                  <Typography className="text-gray-500 text-sm font-medium mb-1">Last Active</Typography>
                  <Typography className="text-gray-900 text-sm">
                    {customer?.lastActiveAt
                      ? formatDateTime(customer.lastActiveAt)
                      : customer?.updatedAt
                        ? new Date(customer.updatedAt).toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })
                        : 'N/A'}
                  </Typography>
                </div>
                <div className="flex-1">
                  <Typography className="text-gray-500 text-sm font-medium mb-1">Upload</Typography>
                  <button
                    className="flex items-center gap-1 text-teal-500 hover:text-teal-600 text-sm"
                    onClick={() => window.open(customer?.national_id_url, '_blank')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z"
                      />
                    </svg>
                    National Id
                  </button>
                </div>
              </div>
            </div>

            {/* GDPR Section */}
            <h2 className="text-lg font-semibold text-gray-900 mb-4">GDPR</h2>
            {activitiesLoading ? (
              <div className="px-6 py-8 text-center">
                <LoadingIndicator />
              </div>
            ) : activitiesError ? (
              <div className="px-6 py-8">
                <ErrorState message={activitiesError} />
              </div>
            ) : gdprConsents && gdprConsents.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 mb-6">
                {gdprConsents.map(notification => (
                  <div key={notification._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      {notification.title || 'GDPR Notification'}
                    </h3>
                    <span
                      className={`inline-block px-3 py-1 text-xs font-medium text-white rounded mb-2 ${
                        notification.status === 'Positive' ? 'bg-[#36b915]' : 'bg-red-500'
                      }`}
                    >
                      {notification.status || 'Pending'}
                    </span>
                    <p className="text-xs text-gray-600 mb-3">{notification.agreedOn || 'N/A'}</p>
                    <p className="text-xs text-gray-600 mb-3">{notification.message || 'No message'}</p>
                    <p className="text-xs text-gray-600">{notification.version || 'N/A'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                <p>No GDPR notifications available</p>
              </div>
            )}

            {/* Activities Section */}
            <div className="py-4">
              <div className="bg-white px-8 py-8">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    className={`px-4 py-2 ${
                      activeTab === tab.id
                        ? 'font-semibold text-dark border-b border-dark focus:ring-0'
                        : 'shadow-transparent text-gray-700 font-semibold no-underline outline-none'
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label} ({recentActivitiesPagination?.total || 0})
                  </button>
                ))}
                {activeTab === 'activity' && (
                  <>
                    {/* pagination */}
                    <div className="my-2 gap-4">
                      <div className="flex items-center justify-end gap-6 mt-4">
                        <div className="" onClick={handleRefresh}>
                          <div className="p-2 border rounded-full border-primary cursor-pointer">
                            <MdRefresh />
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <Typography className="text-secondary">
                            {`${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, recentActivitiesPagination?.total || 0)} of `}
                            <span className="text-primary px-2">{recentActivitiesPagination?.total || 0}</span>
                          </Typography>
                        </div>
                        <div className="flex gap-4 p-2">
                          <button
                            className="disabled:opacity-30"
                            onClick={() => setCurrentPage(p => p - 1)}
                            disabled={currentPage === 1}
                          >
                            <IoIosArrowBack className="text-2xl" />
                          </button>
                          <button
                            className="disabled:opacity-30"
                            onClick={() => setCurrentPage(p => p + 1)}
                            disabled={currentPage >= (recentActivitiesPagination?.pages || 1)}
                          >
                            <IoIosArrowForward className="text-2xl" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {activitiesLoading ? (
                      <div className="px-6 py-8 text-center">
                        <LoadingIndicator />
                      </div>
                    ) : activitiesError ? (
                      <div className="px-6 py-8">
                        <ErrorState message={activitiesError} />
                      </div>
                    ) : recentActivities && recentActivities.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-white">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-normal text-gray-700">Event</th>
                              <th className="px-4 py-3 text-left text-sm font-normal text-gray-700">Sessions</th>
                              <th className="px-4 py-3 text-left text-sm font-normal text-gray-700">Time</th>
                              <th className="px-4 py-3 text-left text-sm font-normal text-gray-700">
                                Avg Time Per Sessions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {recentActivities.map((activity, index) => (
                              <tr key={activity._id || index}>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {activity.activity?.replace(/_/g, ' ') || activity.event || 'N/A'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {activity.sessions || activity.count || 1}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {activity.recentActivity ||
                                    activity.time ||
                                    formatDateTime(activity.timestamp) ||
                                    'N/A'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {activity.avgTime || activity.duration || '00:00'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="px-6 py-8 text-center text-gray-500">
                        <p>No activities found</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

CustomerProfile.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
}

export default CustomerProfile
